import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/site/SEO";
import PageHeader from "@/components/site/PageHeader";
import QuoteCTA from "@/components/site/QuoteCTA";
import { CITIES } from "@/lib/cities";

export default function ServiceAreas() {
  const grouped = CITIES.reduce((acc, c) => {
    (acc[c.stateName] = acc[c.stateName] || []).push(c);
    return acc;
  }, {});

  const placeSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "901 Lots Service Areas",
    itemListElement: CITIES.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://901lots.com/service-areas/${c.slug}`,
      name: `${c.city}, ${c.state}`,
    })),
  };

  return (
    <div data-testid="service-areas-page">
      <SEO
        title="Service Areas — Memphis, Tennessee, Mississippi, Arkansas"
        description="901 Lots provides parking lot striping and sealcoating across 20 Mid-South cities, from Memphis and Germantown to Olive Branch, Southaven, West Memphis, and Jonesboro."
        path="/service-areas"
        jsonLd={placeSchema}
      />

      <PageHeader
        eyebrow="Where we work"
        title="20 Mid-South cities — and growing."
        subtitle="We cover the Memphis metro and reach roughly two hours in every direction — through West Tennessee, North Mississippi, and Eastern Arkansas. Click any city for local details."
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="rounded-lg overflow-hidden border border-slate-200 sticky top-24">
              <img
                src="/img/memphis-skyline.jpg"
                alt="Memphis skyline along the Mississippi River"
                className="w-full h-72 lg:h-96 object-cover"
              />
              <div className="p-5 bg-slate-50">
                <p className="text-sm text-slate-700">
                  Don't see your city? Ask us — we travel for the right
                  project, especially anywhere within a two-hour drive of
                  Memphis.
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8">
            {Object.entries(grouped).map(([stateName, cities]) => (
              <div key={stateName} className="mb-12 last:mb-0">
                <h2 className="font-display font-bold text-slate-900 text-2xl">
                  {stateName}
                </h2>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/service-areas/${c.slug}`}
                      data-testid={`area-link-${c.slug}`}
                      className="group flex items-center justify-between px-4 py-4 bg-slate-50 border border-slate-200 rounded-md hover:border-blue-300 hover:bg-white hover:shadow-sm transition-all"
                    >
                      <span className="flex items-center gap-3">
                        <MapPin className="h-4 w-4 text-orange-600" />
                        <span>
                          <span className="block font-semibold text-slate-900 text-sm">
                            {c.city}, {c.state}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {c.county} · {c.drive}
                          </span>
                        </span>
                      </span>
                      <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-blue-700 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <QuoteCTA />
    </div>
  );
}
